'use client';

import { AppRouterInstance } from 'next/dist/shared/lib/app-router-context.shared-runtime';
import { ChangeEvent, FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { AUTH_EMAIL_OTP_CODE_LENGTH } from '@/constants/auth/login';
import { AuthApprovalStatus } from '@/enums/AuthApprovalStatus';
import { AuthApprovalWatchKind } from '@/enums/AuthApprovalWatchKind';
import { AuthRecoveryCodeDeliveryFlow } from '@/enums/AuthRecoveryCodeDeliveryFlow';
import { AuthRegistrationEmailFlow } from '@/enums/AuthRegistrationEmailFlow';
import { getAuthApprovalRotateDelayMs, watchAuthApprovalStatus } from '@/features/auth/authApprovalRealtime';
import {
    AuthPhoneProvider,
    buildAuthPhonePolicyCountryById,
    buildFirebasePhoneOtpResponse,
    FALLBACK_AUTH_PHONE_POLICY,
    getAuthOtpCodeLength,
    getAuthPhoneCountrySelectOptions,
    getAuthPhonePolicyCountry,
    getAuthPhoneProvider,
    getDefaultAuthPhonePolicyCountryId,
    type AuthPhonePolicy,
} from '@/features/auth/authPhonePolicy';
import { getLocalizedAuthErrorMessage } from '@/features/auth/errorMessages';
import { clearLoadedFirebasePhoneCode, loadFirebasePhoneAuthClient } from '@/features/auth/firebasePhoneAuthLoader';
import { getOtpCooldownState } from '@/features/auth/loginExperience';
import { getCanVerifyOtp } from '@/features/auth/otpFlow';
import type {
    AuthAccountRecoveryCodesRequiredResponse,
    AuthRegistrationEmailRequiredResponse,
    AuthVerifyAuthenticatedResponse,
    AuthVerifyPendingDeviceApprovalResponse,
    AuthVerifyResponse,
    DeviceApprovalStatus,
} from '@/features/auth/types';
import { Dictionary } from '@/features/i18n/dictionaries';
import { SupportedLocale } from '@/features/i18n/logic';
import type { AuthOtpIssueStep, AuthOtpResponse } from '@/interfaces/AuthOtp';
import { getOrCreateBrowserDeviceId } from '@/lib/device/browserDeviceId';

import {
    AuthApiError,
    acknowledgeRecoveryCodes,
    completeFirebasePhoneRecovery,
    completeRecovery,
    getAuthPhonePolicy,
    pollDeviceApproval,
    refreshDeviceApproval,
    sendOtp,
    sendRecoveryOtp,
    sendRecoveryCodesEmail,
    verifyFirebasePhone,
    verifyOtp,
    verifyTrustedDeviceBootstrapEmail,
} from './api';
import {
    getAutomaticOtpSubmitRegistry,
    getNextOtpActionBaseLabel,
    getOtpHintLabel,
    normalizeEmailOtpCode,
    normalizeOtpCode,
    resolveAuthenticatedRedirectPath,
} from './loginOtpFlowHelpers';
import { buildPhoneE164, formatAuthPhoneNumberInput, normalizePhoneNumberForBackend } from './phone';
import { useChangedNumberRecoveryFlow } from './useChangedNumberRecoveryFlow';

type LoginOtpFlowArgs = {
    dictionary: Dictionary['auth'];
    locale: SupportedLocale;
    router: AppRouterInstance;
    targetPath: string;
};

const RECOVERY_CODES_ACK_DELAY_MS = 5_000;

type LoginStep = 'phone' | 'otp' | 'pendingApproval' | 'trustedBootstrapEmail' | 'recoveryOtp' | 'recoveryCodes';

const isTrustedDeviceBootstrapEmailRequired = (
    response: AuthVerifyResponse
): response is AuthRegistrationEmailRequiredResponse & { flow: AuthRegistrationEmailFlow.TrustedDeviceBootstrap } =>
    response.status === 'registration_email_required' && response.flow === AuthRegistrationEmailFlow.TrustedDeviceBootstrap;

const isBlockedRegistrationResponse = (response: AuthVerifyResponse | { status?: string; flow?: string | null }) =>
    response.status === 'registration_profile_required' ||
    response.status === 'registration_recovery_codes_required' ||
    (response.status === 'registration_email_required' && response.flow !== AuthRegistrationEmailFlow.TrustedDeviceBootstrap);

export const useLoginOtpFlow = ({ dictionary, locale, router, targetPath }: LoginOtpFlowArgs) => {
    const [phone, setPhone] = useState('');
    const [authPhonePolicy, setAuthPhonePolicy] = useState<AuthPhonePolicy>(FALLBACK_AUTH_PHONE_POLICY);
    const [countryOptionId, setCountryOptionId] = useState(() => FALLBACK_AUTH_PHONE_POLICY.defaultCountryId);
    const [otpCode, setOtpCode] = useState('');
    const [email, setEmail] = useState('');
    const [emailOtpCode, setEmailOtpCode] = useState('');
    const [recoveryCode, setRecoveryCode] = useState('');
    const [registrationToken, setRegistrationToken] = useState<string | null>(null);
    const [recoveryToken, setRecoveryToken] = useState<string | null>(null);
    const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
    const [recoveryCodesShownAtMs, setRecoveryCodesShownAtMs] = useState<number | null>(null);
    const [recoveryCodesNowMs, setRecoveryCodesNowMs] = useState(() => Date.now());
    const [didCopyRecoveryCodes, setDidCopyRecoveryCodes] = useState(false);
    const [didEmailRecoveryCodes, setDidEmailRecoveryCodes] = useState(false);
    const [isRecoveryCodesDeliverySheetOpen, setIsRecoveryCodesDeliverySheetOpen] = useState(false);
    const [isSendingRecoveryCodesEmail, setIsSendingRecoveryCodesEmail] = useState(false);
    const [shouldCreateRecoveryCodesInboxCopy, setShouldCreateRecoveryCodesInboxCopy] = useState(false);
    const [step, setStep] = useState<LoginStep>('phone');
    const [otpResponse, setOtpResponse] = useState<AuthOtpResponse | null>(null);
    const [otpRequestedAtMs, setOtpRequestedAtMs] = useState<number | null>(null);
    const [otpCooldownNowMs, setOtpCooldownNowMs] = useState(() => Date.now());
    const [pendingApproval, setPendingApproval] = useState<AuthVerifyPendingDeviceApprovalResponse | null>(null);
    const [pendingApprovalStatus, setPendingApprovalStatus] = useState<DeviceApprovalStatus>('pending');
    const [pendingApprovalNowMs, setPendingApprovalNowMs] = useState(() => Date.now());
    const [errorText, setErrorText] = useState('');
    const [, setErrorCode] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [hasAutoSubmittedOtp, setHasAutoSubmittedOtp] = useState(false);
    const hasStartedAutomaticOtpSubmitRef = useRef(false);
    const hasActiveWebOtpRequestRef = useRef(false);
    const hasCompletedAuthenticationRef = useRef(false);
    const hasVerifyRequestInFlightRef = useRef(false);
    const authPhonePolicyCountryById = useMemo(() => buildAuthPhonePolicyCountryById(authPhonePolicy), [authPhonePolicy]);
    const countryOptions = useMemo(() => getAuthPhoneCountrySelectOptions(authPhonePolicy), [authPhonePolicy]);
    const selectedCountry = useMemo(
        () =>
            getAuthPhonePolicyCountry({
                countryById: authPhonePolicyCountryById,
                countryOptionId,
            }) ??
            getAuthPhonePolicyCountry({
                countryById: authPhonePolicyCountryById,
                countryOptionId: getDefaultAuthPhonePolicyCountryId({ policy: authPhonePolicy }),
            })!,
        [authPhonePolicy, authPhonePolicyCountryById, countryOptionId]
    );
    const authPhoneProvider = useMemo(
        () =>
            getAuthPhoneProvider({
                countryById: authPhonePolicyCountryById,
                countryOptionId: selectedCountry.id,
            }),
        [authPhonePolicyCountryById, selectedCountry.id]
    );
    const otpCodeLength = getAuthOtpCodeLength(authPhoneProvider);
    const isFirebasePhoneProvider = authPhoneProvider === AuthPhoneProvider.FirebasePhone;
    const normalizeSelectedPhone = useCallback(
        (value: string) =>
            normalizePhoneNumberForBackend({
                phoneNumber: value,
                countryCode: selectedCountry.countryCode,
                countryOptionId: selectedCountry.id,
            }),
        [selectedCountry.countryCode, selectedCountry.id]
    );
    const automaticOtpSubmitKey = useMemo(() => {
        if (!otpRequestedAtMs) {
            return null;
        }

        return `${selectedCountry.id}:${normalizeSelectedPhone(phone)}:${otpResponse?.deliveryTransport ?? 'call'}:${otpRequestedAtMs}`;
    }, [normalizeSelectedPhone, otpRequestedAtMs, otpResponse?.deliveryTransport, phone, selectedCountry.id]);

    const resetAutomaticOtpSubmit = useCallback(() => {
        if (typeof window !== 'undefined' && automaticOtpSubmitKey) {
            delete getAutomaticOtpSubmitRegistry()[automaticOtpSubmitKey];
        }

        hasStartedAutomaticOtpSubmitRef.current = false;
        hasActiveWebOtpRequestRef.current = false;
        setHasAutoSubmittedOtp(false);
    }, [automaticOtpSubmitKey]);

    const resetVerifySubmitGuards = useCallback(() => {
        hasCompletedAuthenticationRef.current = false;
        hasVerifyRequestInFlightRef.current = false;
    }, []);
    useEffect(() => {
        let isCancelled = false;

        void (async () => {
            try {
                const response = await getAuthPhonePolicy();

                if (isCancelled) {
                    return;
                }

                const defaultCountryId = getDefaultAuthPhonePolicyCountryId({
                    policy: response.authPhonePolicy,
                });

                setAuthPhonePolicy(response.authPhonePolicy);
                setCountryOptionId((currentCountryOptionId) =>
                    response.authPhonePolicy.countries.some((country) => country.id === currentCountryOptionId)
                        ? currentCountryOptionId
                        : defaultCountryId
                );
            } catch {
                if (!isCancelled) {
                    setAuthPhonePolicy(FALLBACK_AUTH_PHONE_POLICY);
                    setCountryOptionId(FALLBACK_AUTH_PHONE_POLICY.defaultCountryId);
                }
            }
        })();

        return () => {
            isCancelled = true;
        };
    }, []);

    const beginAutomaticOtpSubmit = useCallback(() => {
        if (hasStartedAutomaticOtpSubmitRef.current) {
            return false;
        }

        if (typeof window !== 'undefined' && automaticOtpSubmitKey) {
            const automaticOtpSubmitRegistry = getAutomaticOtpSubmitRegistry();

            if (automaticOtpSubmitRegistry[automaticOtpSubmitKey]) {
                return false;
            }

            automaticOtpSubmitRegistry[automaticOtpSubmitKey] = true;
        }

        hasStartedAutomaticOtpSubmitRef.current = true;
        setHasAutoSubmittedOtp(true);

        return true;
    }, [automaticOtpSubmitKey]);

    const otpCooldownState = useMemo(
        () =>
            getOtpCooldownState({
                nowMs: otpCooldownNowMs,
                requestedAtMs: step === 'otp' ? otpRequestedAtMs : null,
            }),
        [otpCooldownNowMs, otpRequestedAtMs, step]
    );
    const canRequestOtp = selectedCountry.enabled && Boolean(normalizeSelectedPhone(phone));
    const canVerifyOtp = getCanVerifyOtp({
        otpCode,
        otpCodeLength,
    });
    const otpVariantHint = getOtpHintLabel({
        dictionary,
        deliveryTransport: otpResponse?.deliveryTransport ?? null,
    });
    const canRequestNextOtpStep = step === 'otp' && otpResponse?.nextStep !== null;
    const nextOtpActionLabel = useMemo(() => {
        const baseLabel = getNextOtpActionBaseLabel({
            dictionary,
            nextStep: otpResponse?.nextStep ?? null,
            nextTransport: otpResponse?.nextTransport ?? null,
        });

        if (!baseLabel) {
            return null;
        }

        if (otpCooldownState.canContinue) {
            return baseLabel;
        }

        return `${baseLabel} · ${dictionary.resendIn.replace('{seconds}', String(otpCooldownState.secondsRemaining))}`;
    }, [dictionary, otpCooldownState.canContinue, otpCooldownState.secondsRemaining, otpResponse?.nextStep, otpResponse?.nextTransport]);

    useEffect(() => {
        if (!otpRequestedAtMs || !canRequestNextOtpStep || otpCooldownState.canContinue) {
            return;
        }

        const intervalId = window.setInterval(() => {
            setOtpCooldownNowMs(Date.now());
        }, 1000);

        return () => {
            window.clearInterval(intervalId);
        };
    }, [canRequestNextOtpStep, otpCooldownState.canContinue, otpRequestedAtMs]);

    const resetAuthRecoveryState = useCallback(() => {
        setEmail('');
        setEmailOtpCode('');
        setRecoveryCode('');
        setRegistrationToken(null);
        setRecoveryToken(null);
        setRecoveryCodes([]);
        setRecoveryCodesShownAtMs(null);
        setDidCopyRecoveryCodes(false);
        setDidEmailRecoveryCodes(false);
        setIsRecoveryCodesDeliverySheetOpen(false);
        setShouldCreateRecoveryCodesInboxCopy(false);
    }, []);

    const handleChangedNumberAuthenticated = useCallback(
        (response: AuthVerifyAuthenticatedResponse) => {
            hasCompletedAuthenticationRef.current = true;
            router.replace(resolveAuthenticatedRedirectPath({ response, targetPath }));
        },
        [router, targetPath]
    );

    const handleChangedNumberRecoveryCodesRequired = useCallback((response: AuthAccountRecoveryCodesRequiredResponse) => {
        setRecoveryToken(response.recoveryToken);
        setRecoveryCodes(response.recoveryCodes);
        setRecoveryCodesShownAtMs(Date.now());
        setRecoveryCodesNowMs(Date.now());
        setDidCopyRecoveryCodes(false);
        setDidEmailRecoveryCodes(false);
        setShouldCreateRecoveryCodesInboxCopy(false);
        setStep('recoveryCodes');
    }, []);

    const { reset: resetChangedNumberRecovery, ...changedNumberRecovery } = useChangedNumberRecoveryFlow({
        countryById: authPhonePolicyCountryById,
        dictionary,
        isSubmitting,
        locale,
        onAuthenticated: handleChangedNumberAuthenticated,
        onRecoveryCodesRequired: handleChangedNumberRecoveryCodesRequired,
        phone,
        selectedCountry,
        setErrorCode,
        setErrorText,
        setIsSubmitting,
    });

    const handlePhoneChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            setErrorText('');
            setErrorCode(null);
            resetAuthRecoveryState();
            resetChangedNumberRecovery();
            setOtpResponse(null);
            setOtpRequestedAtMs(null);
            setPendingApproval(null);
            setPendingApprovalStatus('pending');
            resetAutomaticOtpSubmit();
            resetVerifySubmitGuards();
            clearLoadedFirebasePhoneCode();
            setPhone(
                formatAuthPhoneNumberInput({
                    phoneNumber: event.target.value,
                    countryCode: selectedCountry.countryCode,
                    countryOptionId: selectedCountry.id,
                })
            );
        },
        [
            resetAutomaticOtpSubmit,
            resetAuthRecoveryState,
            resetChangedNumberRecovery,
            resetVerifySubmitGuards,
            selectedCountry.countryCode,
            selectedCountry.id,
        ]
    );

    const handleCountryOptionChange = useCallback(
        (event: ChangeEvent<HTMLSelectElement>) => {
            const nextCountryOptionId = event.target.value;
            const nextCountry = getAuthPhonePolicyCountry({
                countryById: authPhonePolicyCountryById,
                countryOptionId: nextCountryOptionId,
            });

            setErrorText('');
            setErrorCode(null);
            setCountryOptionId(nextCountryOptionId);
            resetAuthRecoveryState();
            resetChangedNumberRecovery();
            setOtpResponse(null);
            setOtpRequestedAtMs(null);
            setPendingApproval(null);
            setPendingApprovalStatus('pending');
            resetAutomaticOtpSubmit();
            resetVerifySubmitGuards();
            clearLoadedFirebasePhoneCode();
            setPhone((currentPhone) =>
                nextCountry
                    ? formatAuthPhoneNumberInput({
                          phoneNumber: currentPhone,
                          countryCode: nextCountry.countryCode,
                          countryOptionId: nextCountry.id,
                      })
                    : ''
            );
        },
        [authPhonePolicyCountryById, resetAutomaticOtpSubmit, resetAuthRecoveryState, resetChangedNumberRecovery, resetVerifySubmitGuards]
    );

    const handleOtpCodeChange = useCallback((nextValue: string) => {
        setErrorText('');
        setErrorCode(null);
        setOtpCode(nextValue);
    }, []);

    const handleEmailOtpCodeChange = useCallback((nextValue: string) => {
        setErrorText('');
        setErrorCode(null);
        setEmailOtpCode(normalizeEmailOtpCode(nextValue));
    }, []);

    const handleRecoveryCodeChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        setErrorText('');
        setErrorCode(null);
        setRecoveryCode(event.target.value);
    }, []);

    const handleChangePhone = useCallback(() => {
        setErrorText('');
        setErrorCode(null);
        setOtpCode('');
        resetAuthRecoveryState();
        resetChangedNumberRecovery();
        setOtpResponse(null);
        setOtpRequestedAtMs(null);
        setPendingApproval(null);
        setPendingApprovalStatus('pending');
        resetAutomaticOtpSubmit();
        resetVerifySubmitGuards();
        clearLoadedFirebasePhoneCode();
        setStep('phone');
    }, [resetAutomaticOtpSubmit, resetAuthRecoveryState, resetChangedNumberRecovery, resetVerifySubmitGuards]);

    const clearAuthError = useCallback(() => {
        setErrorText('');
        setErrorCode(null);
    }, []);

    const requestOtp = useCallback(
        async ({ issueStep }: { issueStep: AuthOtpIssueStep }) => {
            const response = isFirebasePhoneProvider
                ? await (async () => {
                      const firebasePhoneAuth = await loadFirebasePhoneAuthClient();

                      await firebasePhoneAuth.requestFirebasePhoneCode({
                          phoneE164: buildPhoneE164({
                              phoneNumber: phone,
                              countryCode: selectedCountry.countryCode,
                              countryOptionId: selectedCountry.id,
                          }),
                      });

                      return buildFirebasePhoneOtpResponse();
                  })()
                : await sendOtp({
                      phone: normalizeSelectedPhone(phone),
                      countryOptionId: selectedCountry.id,
                      issueStep,
                  });

            if (response.isRegister) {
                setOtpCode('');
                setOtpResponse(null);
                setOtpRequestedAtMs(null);
                setOtpCooldownNowMs(Date.now());
                setPendingApproval(null);
                setPendingApprovalStatus('pending');
                resetAutomaticOtpSubmit();
                setErrorText(dictionary.mobileRegistrationRequired);
                setStep('phone');
                return;
            }

            resetVerifySubmitGuards();
            setOtpCode('');
            setOtpResponse(response);
            setOtpRequestedAtMs(Date.now());
            setOtpCooldownNowMs(Date.now());
            setPendingApproval(null);
            setPendingApprovalStatus('pending');
            resetAutomaticOtpSubmit();
            setStep('otp');
        },
        [
            dictionary.mobileRegistrationRequired,
            isFirebasePhoneProvider,
            normalizeSelectedPhone,
            phone,
            resetAutomaticOtpSubmit,
            resetVerifySubmitGuards,
            selectedCountry.countryCode,
            selectedCountry.id,
        ]
    );

    const submitRequestOtp = useCallback(async () => {
        setIsSubmitting(true);
        setErrorText('');
        setErrorCode(null);

        try {
            await requestOtp({
                issueStep: 'initial',
            });
        } catch (error) {
            setErrorCode(error instanceof AuthApiError ? error.code : null);
            setErrorText(
                getLocalizedAuthErrorMessage({
                    error,
                    locale,
                    fallback: dictionary.requestOtpFailed,
                    isRegistrationFlow: false,
                })
            );
        } finally {
            setIsSubmitting(false);
        }
    }, [dictionary.requestOtpFailed, locale, requestOtp]);

    const onRequestOtp = useCallback(
        async (event: FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            await submitRequestOtp();
        },
        [submitRequestOtp]
    );

    const submitVerifyOtp = useCallback(
        async ({ otpCodeOverride, automatic = false }: { otpCodeOverride?: string; automatic?: boolean } = {}) => {
            if (hasCompletedAuthenticationRef.current || hasVerifyRequestInFlightRef.current) {
                return;
            }

            const resolvedOtpCode = (otpCodeOverride ?? otpCode).trim();

            if (
                !getCanVerifyOtp({
                    otpCode: resolvedOtpCode,
                    otpCodeLength,
                })
            ) {
                return;
            }

            if (automatic && !hasStartedAutomaticOtpSubmitRef.current) {
                hasStartedAutomaticOtpSubmitRef.current = true;
                setHasAutoSubmittedOtp(true);
            }

            hasVerifyRequestInFlightRef.current = true;
            setIsSubmitting(true);
            setErrorText('');

            try {
                setErrorCode(null);
                const deviceId = getOrCreateBrowserDeviceId();
                const response = isFirebasePhoneProvider
                    ? await (async () => {
                          const firebasePhoneAuth = await loadFirebasePhoneAuthClient();
                          const { idToken } = await firebasePhoneAuth.confirmFirebasePhoneCode({
                              otpCode: resolvedOtpCode,
                          });

                          return verifyFirebasePhone({
                              idToken,
                              countryOptionId: selectedCountry.id,
                              deviceId,
                          });
                      })()
                    : await verifyOtp({
                          phone: normalizeSelectedPhone(phone),
                          otpCode: resolvedOtpCode,
                          countryOptionId: selectedCountry.id,
                          deviceId,
                      });

                if (response.status === 'pending_device_approval') {
                    setPendingApproval(response);
                    setPendingApprovalStatus('pending');
                    setPendingApprovalNowMs(Date.now());
                    resetAutomaticOtpSubmit();
                    setStep('pendingApproval');
                    return;
                }

                if (isTrustedDeviceBootstrapEmailRequired(response)) {
                    setRegistrationToken(response.registrationToken);
                    setEmail(response.email);
                    setEmailOtpCode('');
                    resetAutomaticOtpSubmit();
                    setStep('trustedBootstrapEmail');
                    return;
                }

                if (isBlockedRegistrationResponse(response)) {
                    setRegistrationToken(null);
                    setEmail('');
                    setEmailOtpCode('');
                    resetAutomaticOtpSubmit();
                    setErrorText(dictionary.mobileRegistrationRequired);
                    setStep('phone');
                    return;
                }

                hasCompletedAuthenticationRef.current = true;
                router.replace(targetPath);
            } catch (error) {
                setErrorCode(error instanceof AuthApiError ? error.code : null);
                setErrorText(
                    getLocalizedAuthErrorMessage({
                        error,
                        locale,
                        fallback: dictionary.verifyOtpFailed,
                        isRegistrationFlow: false,
                    })
                );
            } finally {
                hasVerifyRequestInFlightRef.current = false;
                setIsSubmitting(false);
            }
        },
        [
            dictionary.mobileRegistrationRequired,
            dictionary.verifyOtpFailed,
            isFirebasePhoneProvider,
            locale,
            normalizeSelectedPhone,
            otpCode,
            otpCodeLength,
            phone,
            resetAutomaticOtpSubmit,
            router,
            selectedCountry.id,
            targetPath,
        ]
    );

    useEffect(() => {
        if (step !== 'pendingApproval' || !pendingApproval || hasCompletedAuthenticationRef.current) {
            return;
        }

        let isCancelled = false;
        const deviceId = getOrCreateBrowserDeviceId();

        const finalizeApprovedDeviceApproval = async ({ token }: { token: string }) => {
            try {
                const response = await pollDeviceApproval({
                    token,
                    deviceId,
                });

                if (isCancelled) {
                    return;
                }

                setPendingApprovalStatus(response.status);
                setPendingApprovalNowMs(Date.now());

                if (response.status === AuthApprovalStatus.Approved || response.status === AuthApprovalStatus.Consumed) {
                    hasCompletedAuthenticationRef.current = true;
                    router.replace(targetPath);
                    return;
                }

                if (response.status === AuthApprovalStatus.Expired) {
                    setErrorText(dictionary.pendingDeviceApprovalExpired);
                } else if (response.status === AuthApprovalStatus.Rejected) {
                    setErrorText(dictionary.pendingDeviceApprovalRejected);
                } else if (response.status === AuthApprovalStatus.NotFound) {
                    setErrorText(dictionary.pendingDeviceApprovalNotFound);
                }
            } catch (error) {
                if (!isCancelled) {
                    setErrorCode(error instanceof AuthApiError ? error.code : null);
                    setErrorText(
                        getLocalizedAuthErrorMessage({
                            error,
                            locale,
                            fallback: dictionary.pendingDeviceApprovalPollFailed,
                            isRegistrationFlow: false,
                        })
                    );
                }
            }
        };

        const refreshCurrentDeviceApproval = async () => {
            try {
                const response = await refreshDeviceApproval({
                    token: pendingApproval.approvalToken,
                    deviceId,
                });

                if (isCancelled) {
                    return;
                }

                setPendingApprovalNowMs(Date.now());

                if (response.status === 'pending_device_approval') {
                    setPendingApproval(response);
                    setPendingApprovalStatus(AuthApprovalStatus.Pending);
                    setErrorText('');
                    return;
                }

                setPendingApprovalStatus(response.status);

                if (response.status === AuthApprovalStatus.Approved || response.status === AuthApprovalStatus.Consumed) {
                    await finalizeApprovedDeviceApproval({
                        token: pendingApproval.approvalToken,
                    });
                    return;
                }

                if (response.status === AuthApprovalStatus.Rejected) {
                    setErrorText(dictionary.pendingDeviceApprovalRejected);
                } else if (response.status === AuthApprovalStatus.NotFound) {
                    setErrorText(dictionary.pendingDeviceApprovalNotFound);
                }
            } catch (error) {
                if (!isCancelled) {
                    setErrorCode(error instanceof AuthApiError ? error.code : null);
                    setErrorText(
                        getLocalizedAuthErrorMessage({
                            error,
                            locale,
                            fallback: dictionary.pendingDeviceApprovalPollFailed,
                            isRegistrationFlow: false,
                        })
                    );
                }
            }
        };

        const stopWatchingApproval = watchAuthApprovalStatus({
            deviceId,
            kind: AuthApprovalWatchKind.DeviceApproval,
            token: pendingApproval.approvalToken,
            onStatus: (payload) => {
                if (isCancelled || payload.token !== pendingApproval.approvalToken) {
                    return;
                }

                setPendingApprovalNowMs(Date.now());

                if (payload.status === AuthApprovalStatus.Expired) {
                    void refreshCurrentDeviceApproval();
                    return;
                }

                setPendingApprovalStatus(payload.status);

                if (payload.status === AuthApprovalStatus.Approved || payload.status === AuthApprovalStatus.Consumed) {
                    void finalizeApprovedDeviceApproval({
                        token: pendingApproval.approvalToken,
                    });
                    return;
                }

                if (payload.status === AuthApprovalStatus.Rejected) {
                    setErrorText(dictionary.pendingDeviceApprovalRejected);
                } else if (payload.status === AuthApprovalStatus.NotFound) {
                    setErrorText(dictionary.pendingDeviceApprovalNotFound);
                }
            },
        });
        const refreshTimeoutId = window.setTimeout(
            () => {
                void refreshCurrentDeviceApproval();
            },
            getAuthApprovalRotateDelayMs({ expiresAt: pendingApproval.expiresAt })
        );

        return () => {
            isCancelled = true;
            stopWatchingApproval();
            window.clearTimeout(refreshTimeoutId);
        };
    }, [
        dictionary.pendingDeviceApprovalExpired,
        dictionary.pendingDeviceApprovalNotFound,
        dictionary.pendingDeviceApprovalPollFailed,
        dictionary.pendingDeviceApprovalRejected,
        locale,
        pendingApproval,
        router,
        step,
        targetPath,
    ]);

    useEffect(() => {
        if (step !== 'pendingApproval' || !pendingApproval) {
            return;
        }

        const intervalId = window.setInterval(() => {
            setPendingApprovalNowMs(Date.now());
        }, 1000);

        return () => {
            window.clearInterval(intervalId);
        };
    }, [pendingApproval, step]);

    const pendingApprovalSecondsRemaining = useMemo(() => {
        if (!pendingApproval) {
            return 0;
        }

        const expiresAtMs = new Date(pendingApproval.expiresAt).getTime();
        return Math.max(0, Math.ceil((expiresAtMs - pendingApprovalNowMs) / 1000));
    }, [pendingApproval, pendingApprovalNowMs]);
    const recoveryCodesAckSecondsRemaining = useMemo(() => {
        if (!recoveryCodesShownAtMs) {
            return 0;
        }

        const ackAtMs = recoveryCodesShownAtMs + RECOVERY_CODES_ACK_DELAY_MS;
        return Math.max(0, Math.ceil((ackAtMs - recoveryCodesNowMs) / 1000));
    }, [recoveryCodesNowMs, recoveryCodesShownAtMs]);
    const canVerifyEmailOtp = emailOtpCode.length === AUTH_EMAIL_OTP_CODE_LENGTH && Boolean(registrationToken);
    const canSubmitRecovery = otpCode.length === otpCodeLength && recoveryCode.trim().length > 0;
    const canAcknowledgeRecoveryCodes = recoveryCodes.length > 0 && recoveryCodesAckSecondsRemaining === 0;

    useEffect(() => {
        if (step !== 'recoveryCodes' || !recoveryCodesShownAtMs || canAcknowledgeRecoveryCodes) {
            return;
        }

        const intervalId = window.setInterval(() => {
            setRecoveryCodesNowMs(Date.now());
        }, 1000);

        return () => {
            window.clearInterval(intervalId);
        };
    }, [canAcknowledgeRecoveryCodes, recoveryCodesShownAtMs, step]);

    useEffect(() => {
        if (step !== 'otp' || otpResponse?.deliveryTransport !== 'sms' || !otpRequestedAtMs || typeof window === 'undefined') {
            return;
        }

        if (!('OTPCredential' in window) || typeof navigator.credentials?.get !== 'function') {
            return;
        }

        if (hasStartedAutomaticOtpSubmitRef.current) {
            return;
        }

        const abortController = new AbortController();
        hasActiveWebOtpRequestRef.current = true;

        void (async () => {
            try {
                const credential = (await navigator.credentials.get({
                    otp: {
                        transport: ['sms'],
                    },
                    signal: abortController.signal,
                } as unknown as CredentialRequestOptions)) as { code?: string | null } | null;
                const nextOtpCode = normalizeOtpCode(String(credential?.code || ''), otpCodeLength);

                if (nextOtpCode.length !== otpCodeLength) {
                    hasActiveWebOtpRequestRef.current = false;
                    return;
                }

                setOtpCode(nextOtpCode);
                setErrorText('');
                setErrorCode(null);

                if (
                    !getCanVerifyOtp({
                        otpCode: nextOtpCode,
                        otpCodeLength,
                    })
                ) {
                    hasActiveWebOtpRequestRef.current = false;
                    return;
                }

                if (!beginAutomaticOtpSubmit()) {
                    hasActiveWebOtpRequestRef.current = false;
                    return;
                }

                await submitVerifyOtp({
                    otpCodeOverride: nextOtpCode,
                    automatic: true,
                });
            } catch (error) {
                if (error instanceof DOMException && error.name === 'AbortError') {
                    return;
                }

                hasActiveWebOtpRequestRef.current = false;
            } finally {
                hasActiveWebOtpRequestRef.current = false;
            }
        })();

        return () => {
            hasActiveWebOtpRequestRef.current = false;
            abortController.abort();
        };
    }, [beginAutomaticOtpSubmit, otpCodeLength, otpRequestedAtMs, otpResponse?.deliveryTransport, step, submitVerifyOtp]);

    const handleOtpCodeComplete = useCallback(
        (completedOtpCode: string) => {
            if (hasActiveWebOtpRequestRef.current || hasAutoSubmittedOtp || isSubmitting) {
                return;
            }

            if (
                !getCanVerifyOtp({
                    otpCode: completedOtpCode,
                    otpCodeLength,
                })
            ) {
                return;
            }

            if (!beginAutomaticOtpSubmit()) {
                return;
            }

            void submitVerifyOtp({
                otpCodeOverride: completedOtpCode,
                automatic: true,
            });
        },
        [beginAutomaticOtpSubmit, hasAutoSubmittedOtp, isSubmitting, otpCodeLength, submitVerifyOtp]
    );

    const verifyCurrentOtp = useCallback(async () => {
        await submitVerifyOtp();
    }, [submitVerifyOtp]);

    const onVerifyOtp = useCallback(
        async (event: FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            await verifyCurrentOtp();
        },
        [verifyCurrentOtp]
    );

    const handleRequestNextOtpStep = useCallback(async () => {
        if (!otpCooldownState.canContinue || !otpResponse?.nextStep) {
            return;
        }

        setIsSubmitting(true);
        setErrorText('');
        setErrorCode(null);
        setOtpCode('');

        try {
            await requestOtp({
                issueStep: otpResponse.nextStep,
            });
        } catch (error) {
            setErrorCode(error instanceof AuthApiError ? error.code : null);
            setErrorText(
                getLocalizedAuthErrorMessage({
                    error,
                    locale,
                    fallback: dictionary.requestOtpFailed,
                    isRegistrationFlow: false,
                })
            );
        } finally {
            setIsSubmitting(false);
        }
    }, [dictionary.requestOtpFailed, locale, otpCooldownState.canContinue, otpResponse?.nextStep, requestOtp]);

    const handleRequestNextOtpStepClick = useCallback(() => {
        void handleRequestNextOtpStep();
    }, [handleRequestNextOtpStep]);

    const handleStartRecoveryClick = useCallback(async () => {
        if (!canRequestOtp || isSubmitting) {
            return;
        }

        setIsSubmitting(true);
        setErrorText('');
        setErrorCode(null);

        try {
            const response = isFirebasePhoneProvider
                ? await (async () => {
                      const firebasePhoneAuth = await loadFirebasePhoneAuthClient();

                      await firebasePhoneAuth.requestFirebasePhoneCode({
                          phoneE164: buildPhoneE164({
                              phoneNumber: phone,
                              countryCode: selectedCountry.countryCode,
                              countryOptionId: selectedCountry.id,
                          }),
                      });

                      return buildFirebasePhoneOtpResponse();
                  })()
                : await sendRecoveryOtp({
                      phone: normalizeSelectedPhone(phone),
                      countryOptionId: selectedCountry.id,
                      issueStep: 'initial',
                  });

            setOtpCode('');
            setOtpResponse(response);
            setOtpRequestedAtMs(Date.now());
            setOtpCooldownNowMs(Date.now());
            setRecoveryCode('');
            setStep('recoveryOtp');
        } catch (error) {
            setErrorCode(error instanceof AuthApiError ? error.code : null);
            setErrorText(
                getLocalizedAuthErrorMessage({
                    error,
                    locale,
                    fallback: dictionary.requestOtpFailed,
                    isRegistrationFlow: false,
                })
            );
        } finally {
            setIsSubmitting(false);
        }
    }, [
        canRequestOtp,
        dictionary.requestOtpFailed,
        isFirebasePhoneProvider,
        isSubmitting,
        locale,
        normalizeSelectedPhone,
        phone,
        selectedCountry.countryCode,
        selectedCountry.id,
    ]);

    const submitTrustedDeviceBootstrapEmailOtp = useCallback(async () => {
        if (!registrationToken || !canVerifyEmailOtp || isSubmitting) {
            return;
        }

        setIsSubmitting(true);
        setErrorText('');
        setErrorCode(null);

        try {
            const response = await verifyTrustedDeviceBootstrapEmail({
                registrationToken,
                emailOtpCode,
            });

            hasCompletedAuthenticationRef.current = true;
            router.replace(resolveAuthenticatedRedirectPath({ response, targetPath }));
        } catch (error) {
            setErrorCode(error instanceof AuthApiError ? error.code : null);
            setErrorText(
                getLocalizedAuthErrorMessage({
                    error,
                    locale,
                    fallback: dictionary.verifyOtpFailed,
                    isRegistrationFlow: false,
                })
            );
        } finally {
            setIsSubmitting(false);
        }
    }, [canVerifyEmailOtp, dictionary.verifyOtpFailed, emailOtpCode, isSubmitting, locale, registrationToken, router, targetPath]);

    const onVerifyTrustedDeviceBootstrapEmail = useCallback(
        async (event: FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            await submitTrustedDeviceBootstrapEmailOtp();
        },
        [submitTrustedDeviceBootstrapEmailOtp]
    );

    const submitRecovery = useCallback(async () => {
        if (!canSubmitRecovery || isSubmitting) {
            return;
        }

        setIsSubmitting(true);
        setErrorText('');
        setErrorCode(null);

        try {
            const deviceId = getOrCreateBrowserDeviceId();
            const response = isFirebasePhoneProvider
                ? await (async () => {
                      const firebasePhoneAuth = await loadFirebasePhoneAuthClient();
                      const { idToken } = await firebasePhoneAuth.confirmFirebasePhoneCode({
                          otpCode: otpCode.trim(),
                      });

                      return completeFirebasePhoneRecovery({
                          idToken,
                          countryOptionId: selectedCountry.id,
                          recoveryCode: recoveryCode.trim(),
                          deviceId,
                      });
                  })()
                : await completeRecovery({
                      phone: normalizeSelectedPhone(phone),
                      otpCode: otpCode.trim(),
                      countryOptionId: selectedCountry.id,
                      recoveryCode: recoveryCode.trim(),
                      deviceId,
                  });

            setRecoveryToken(response.recoveryToken);
            setRecoveryCodes(response.recoveryCodes);
            setRecoveryCodesShownAtMs(Date.now());
            setRecoveryCodesNowMs(Date.now());
            setDidCopyRecoveryCodes(false);
            setDidEmailRecoveryCodes(false);
            setShouldCreateRecoveryCodesInboxCopy(false);
            setStep('recoveryCodes');
        } catch (error) {
            setErrorCode(error instanceof AuthApiError ? error.code : null);
            setErrorText(
                getLocalizedAuthErrorMessage({
                    error,
                    locale,
                    fallback: dictionary.verifyOtpFailed,
                    isRegistrationFlow: false,
                })
            );
        } finally {
            setIsSubmitting(false);
        }
    }, [
        canSubmitRecovery,
        dictionary.verifyOtpFailed,
        isFirebasePhoneProvider,
        isSubmitting,
        locale,
        normalizeSelectedPhone,
        otpCode,
        phone,
        recoveryCode,
        selectedCountry.id,
    ]);

    const onCompleteRecovery = useCallback(
        async (event: FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            await submitRecovery();
        },
        [submitRecovery]
    );

    const handleCopyRecoveryCodes = useCallback(async () => {
        if (!recoveryCodes.length || typeof navigator === 'undefined' || !navigator.clipboard?.writeText) {
            return;
        }

        await navigator.clipboard.writeText(recoveryCodes.join('\n'));
        setDidCopyRecoveryCodes(true);
    }, [recoveryCodes]);

    const handleOpenRecoveryCodesDeliverySheet = useCallback(() => {
        setIsRecoveryCodesDeliverySheetOpen(true);
    }, []);

    const handleCloseRecoveryCodesDeliverySheet = useCallback(() => {
        if (!isSendingRecoveryCodesEmail) {
            setIsRecoveryCodesDeliverySheetOpen(false);
        }
    }, [isSendingRecoveryCodesEmail]);

    const handleRecoveryCodesInboxCopyChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        setShouldCreateRecoveryCodesInboxCopy(event.target.checked);
    }, []);

    const acknowledgeDisplayedRecoveryCodes = useCallback(async () => {
        if (!canAcknowledgeRecoveryCodes || isSubmitting) {
            return;
        }

        setIsSubmitting(true);
        setErrorText('');
        setErrorCode(null);

        try {
            if (!recoveryToken) {
                throw new Error('missing_recovery_token');
            }

            const response = await acknowledgeRecoveryCodes({
                recoveryToken,
                deviceId: getOrCreateBrowserDeviceId(),
                recoveryCodeDelivery: shouldCreateRecoveryCodesInboxCopy
                    ? {
                          recoveryCodes,
                          createInboxCopy: true,
                      }
                    : undefined,
            });
            hasCompletedAuthenticationRef.current = true;
            router.replace(resolveAuthenticatedRedirectPath({ response, targetPath }));
        } catch (error) {
            setErrorCode(error instanceof AuthApiError ? error.code : null);
            setErrorText(
                getLocalizedAuthErrorMessage({
                    error,
                    locale,
                    fallback: dictionary.verifyOtpFailed,
                    isRegistrationFlow: false,
                })
            );
        } finally {
            setIsSubmitting(false);
        }
    }, [
        canAcknowledgeRecoveryCodes,
        dictionary.verifyOtpFailed,
        isSubmitting,
        locale,
        recoveryCodes,
        recoveryToken,
        router,
        shouldCreateRecoveryCodesInboxCopy,
        targetPath,
    ]);

    const handleSendRecoveryCodesEmail = useCallback(async () => {
        if (isSendingRecoveryCodesEmail || isSubmitting || !recoveryCodes.length) {
            return;
        }

        setIsSendingRecoveryCodesEmail(true);
        setErrorText('');
        setErrorCode(null);

        try {
            if (!recoveryToken) {
                throw new Error('missing_recovery_token');
            }

            await sendRecoveryCodesEmail({
                flow: AuthRecoveryCodeDeliveryFlow.AccountRecovery,
                recoveryToken,
                recoveryCodes,
                ...(shouldCreateRecoveryCodesInboxCopy
                    ? {
                          recoveryCodeDelivery: {
                              recoveryCodes,
                              createInboxCopy: true,
                          },
                      }
                    : {}),
            });

            setDidEmailRecoveryCodes(true);
            if (shouldCreateRecoveryCodesInboxCopy && canAcknowledgeRecoveryCodes) {
                setIsRecoveryCodesDeliverySheetOpen(false);
                await acknowledgeDisplayedRecoveryCodes();
            }
        } catch (error) {
            setErrorCode(error instanceof AuthApiError ? error.code : null);
            setErrorText(
                getLocalizedAuthErrorMessage({
                    error,
                    locale,
                    fallback: dictionary.verifyOtpFailed,
                    isRegistrationFlow: false,
                })
            );
        } finally {
            setIsSendingRecoveryCodesEmail(false);
        }
    }, [
        acknowledgeDisplayedRecoveryCodes,
        canAcknowledgeRecoveryCodes,
        dictionary.verifyOtpFailed,
        isSubmitting,
        isSendingRecoveryCodesEmail,
        locale,
        recoveryCodes,
        recoveryToken,
        shouldCreateRecoveryCodesInboxCopy,
    ]);

    const handleAcknowledgeRecoveryCodesClick = useCallback(() => {
        void acknowledgeDisplayedRecoveryCodes();
    }, [acknowledgeDisplayedRecoveryCodes]);
    return {
        canRequestNextOtpStep,
        canRequestOtp,
        canAcknowledgeRecoveryCodes,
        canSubmitRecovery,
        canVerifyEmailOtp,
        canVerifyOtp,
        clearAuthError,
        countryOptionId,
        countryOptions,
        ...changedNumberRecovery,
        didCopyRecoveryCodes,
        didEmailRecoveryCodes,
        email,
        emailOtpCode,
        errorText,
        handleAcknowledgeRecoveryCodesClick,
        handleChangePhone,
        handleCloseRecoveryCodesDeliverySheet,
        handleCopyRecoveryCodes,
        handleEmailOtpCodeChange,
        handleCountryOptionChange,
        handleOpenRecoveryCodesDeliverySheet,
        handleOtpCodeComplete,
        handleOtpCodeChange,
        handlePhoneChange,
        handleRecoveryCodesInboxCopyChange,
        handleRequestNextOtpStepClick,
        handleRecoveryCodeChange,
        handleSendRecoveryCodesEmail,
        handleStartRecoveryClick,
        isRecoveryCodesDeliverySheetOpen,
        isSendingRecoveryCodesEmail,
        isSubmitting,
        nextOtpActionLabel,
        onCompleteRecovery,
        onRequestOtp,
        onVerifyTrustedDeviceBootstrapEmail,
        onVerifyOtp,
        otpCode,
        otpCodeLength,
        otpCooldownState,
        otpVariantHint,
        pendingApproval,
        pendingApprovalSecondsRemaining,
        pendingApprovalStatus,
        phone,
        recoveryCode,
        recoveryCodes,
        recoveryCodesAckSecondsRemaining,
        shouldCreateRecoveryCodesInboxCopy,
        step,
        submitRequestOtp,
    };
};
